/**
 * The site's node pools (JSS#1104, ADR-JSS-0011): which boxes run which
 * workload classes. An Admin proposes or edits the layout, moves boxes
 * between pools, and walks the site through the stages
 * (Off → Labelled → Enforced → Dedicated), each with an impact preview;
 * Engineers see it read-only. The conformance list shows pods the layout no
 * longer places where they run, and Reschedule restarts the ones picked.
 *
 * A pool is a fence, not a placer: it only removes nodes a class may use.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Lock, Plus, RefreshCw, Trash2, Wand2, XCircle } from 'lucide-react';
import { Role, canActAs } from '@jeket-cloud/types/auth';
import type {
  NodePoolLayout, NodePoolProblem, NodePoolsErrorBody, NodePoolStage, NodePoolsView,
} from '@jeket-cloud/types';
import { nodePoolsApi } from '@/api/client';
import { useUserRoles } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import {
  CLASS_DESCRIPTIONS, CONFORMANCE_REASON_TEXT, NODE_POOL_STAGES, STAGE_TEXT, layoutOf, nodePoolsError,
} from '@/lib/nodePools';
import NodePoolStageDialog from './NodePoolStageDialog';

const CARD = 'bg-surface border border-surface-elevated rounded-xl';

const RESCHEDULE_TEXT: Record<string, string> = {
  restart_owner: 'Reschedule restarts it',
  delete_pod: 'Reschedule deletes the pod; its controller recreates it',
  none: 'restart it through its own controller',
};
const BUTTON = 'px-3 py-1.5 text-xs font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
const PRIMARY = `${BUTTON} bg-gold text-surface-deep hover:bg-gold-light`;
const SECONDARY = `${BUTTON} border border-surface-elevated text-silver-bright hover:border-gold/60`;

interface Props {
  siteId: string;
}

export default function NodePoolsPanel({ siteId }: Props) {
  const roles = useUserRoles();
  const canEdit = canActAs(roles, Role.Admin);
  const queryClient = useQueryClient();
  const { data: view, isLoading, error } = useQuery({
    queryKey: ['node-pools', siteId],
    queryFn: () => nodePoolsApi.get(siteId),
    refetchInterval: 15_000,
  });
  const [draft, setDraft] = useState<NodePoolLayout | null>(null);
  const [saveError, setSaveError] = useState<NodePoolsErrorBody | null>(null);
  const [stageTarget, setStageTarget] = useState<NodePoolStage | null>(null);

  const save = useMutation({
    mutationFn: ({ layout, revision }: { layout: NodePoolLayout; revision: number }) => nodePoolsApi.save(siteId, layout, revision),
    onSuccess: () => {
      setDraft(null);
      setSaveError(null);
      void queryClient.invalidateQueries({ queryKey: ['node-pools', siteId] });
      void queryClient.invalidateQueries({ queryKey: ['cluster-nodes', siteId] });
    },
    onError: (err) => setSaveError(nodePoolsError(err)),
  });

  if (isLoading) {
    return <div className={cn(CARD, 'p-8 animate-pulse')} data-testid="node-pools-loading"><div className="h-24 bg-surface-elevated rounded" /></div>;
  }
  if (error || !view) {
    return (
      <div className={cn(CARD, 'p-6 text-sm text-status-error')} role="alert">
        Could not load the node pools: {nodePoolsError(error).error}
      </div>
    );
  }

  const editing = draft !== null;
  return (
    <div className="space-y-4" data-testid="node-pools-panel">
      <StageCard view={view} canEdit={canEdit && !editing} onMove={setStageTarget} />
      <ValidationCard problems={view.validation.problems} warnings={view.validation.warnings} />
      <LayoutCard
        view={view}
        draft={draft}
        canEdit={canEdit}
        saving={save.isPending}
        saveError={saveError}
        onEdit={() => { setDraft(layoutOf(view)); setSaveError(null); }}
        onPropose={() => { setDraft(layoutOf(view.proposal.layout)); setSaveError(null); }}
        onChange={setDraft}
        onCancel={() => { setDraft(null); setSaveError(null); }}
        onSave={() => draft && save.mutate({ layout: draft, revision: view.revision })}
      />
      <ConformanceCard siteId={siteId} view={view} canEdit={canEdit} />
      {stageTarget && (
        <NodePoolStageDialog
          siteId={siteId}
          view={view}
          stage={stageTarget}
          onClose={() => setStageTarget(null)}
          onDone={() => {
            setStageTarget(null);
            void queryClient.invalidateQueries({ queryKey: ['node-pools', siteId] });
          }}
        />
      )}
    </div>
  );
}

// ─── Stage ──────────────────────────────────────────────────────────────────

function StageCard({ view, canEdit, onMove }: { view: NodePoolsView; canEdit: boolean; onMove: (s: NodePoolStage) => void }) {
  const status = view.status;
  const i = NODE_POOL_STAGES.indexOf(view.stage);
  const next = NODE_POOL_STAGES[i + 1];
  const prev = NODE_POOL_STAGES[i - 1];
  const reached = status?.reachedStage ?? 'none';
  const settled = view.stage === 'none' ? !status || status.converged : status?.targetStage === view.stage && status.converged;
  return (
    <div className={cn(CARD, 'p-5 space-y-4')} data-testid="node-pools-stage">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-silver-bright font-semibold">Node pools</h3>
          <p className="text-xs text-silver-muted max-w-2xl">
            Decide which boxes run which kinds of workload. A pool only narrows where a class may run; it never picks a box.
          </p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            {prev && <button type="button" className={SECONDARY} onClick={() => onMove(prev)}>Back to {STAGE_TEXT[prev].label}</button>}
            {next && <button type="button" className={PRIMARY} onClick={() => onMove(next)}>Move to {STAGE_TEXT[next].label}</button>}
          </div>
        )}
      </div>

      <ol className="grid grid-cols-4 gap-2" aria-label="Stages">
        {NODE_POOL_STAGES.map((s, idx) => (
          <li
            key={s}
            data-testid={`stage-${s}`}
            data-current={s === view.stage}
            className={cn(
              'rounded-lg border px-3 py-2',
              s === view.stage ? 'border-gold bg-gold/10' : idx < i ? 'border-gold/30' : 'border-surface-elevated',
            )}
          >
            <div className="text-xs font-semibold text-silver-bright">{STAGE_TEXT[s].label}</div>
            <div className="text-[11px] text-silver-muted">{STAGE_TEXT[s].description}</div>
          </li>
        ))}
      </ol>

      <div className="text-xs space-y-1" data-testid="node-pools-observed">
        {settled ? (
          <p className="flex items-center gap-1.5 text-status-healthy"><CheckCircle2 size={13} /> The cluster is at {STAGE_TEXT[view.stage].label}.</p>
        ) : (
          <p className="flex items-center gap-1.5 text-status-warning">
            <RefreshCw size={13} /> Asked for {STAGE_TEXT[view.stage].label}; the cluster is at {STAGE_TEXT[reached].label}.
          </p>
        )}
        {status?.probe && (
          <p className={status.probe.ok ? 'text-silver-muted' : 'text-status-error'}>
            Admission probe: {status.probe.ok ? 'pods are fenced' : status.probe.message}
          </p>
        )}
        {status?.apiServed === false && (
          <p className="text-status-error">This cluster cannot enforce pools: it does not serve MutatingAdmissionPolicy (Kubernetes 1.36+).</p>
        )}
        {(status?.blocked ?? []).map((b) => (
          <p key={b} className="flex items-start gap-1.5 text-status-warning"><AlertTriangle size={13} className="mt-0.5 shrink-0" />{b}</p>
        ))}
        {(status?.daemonSetGaps ?? []).filter((g) => g.known).map((g) => (
          <p key={`${g.namespace}/${g.name}`} className="text-silver-muted">
            {g.namespace}/{g.name} does not tolerate the pool taint: it keeps running on {g.nodes.join(', ')}, but would not come back there if its pod were removed.
          </p>
        ))}
        {status?.error && <p className="text-status-error" role="alert">Last pass failed: {status.error}</p>}
      </div>
    </div>
  );
}

// ─── Problems ───────────────────────────────────────────────────────────────

function ProblemList({ items, tone }: { items: NodePoolProblem[]; tone: 'error' | 'warning' }) {
  return (
    <ul className="space-y-1">
      {items.map((p, i) => (
        <li key={`${p.code}-${i}`} className={cn('flex items-start gap-1.5 text-xs', tone === 'error' ? 'text-status-error' : 'text-status-warning')}>
          {tone === 'error' ? <XCircle size={13} className="mt-0.5 shrink-0" /> : <AlertTriangle size={13} className="mt-0.5 shrink-0" />}
          {p.message}
        </li>
      ))}
    </ul>
  );
}

function ValidationCard({ problems, warnings }: { problems: NodePoolProblem[]; warnings: NodePoolProblem[] }) {
  if (problems.length === 0 && warnings.length === 0) return null;
  return (
    <div className={cn(CARD, 'p-4 space-y-2')} data-testid="node-pools-validation">
      {problems.length > 0 && (
        <>
          <p className="text-xs font-semibold text-status-error">The layout has problems; the site cannot move up a stage until they are fixed.</p>
          <ProblemList items={problems} tone="error" />
        </>
      )}
      {warnings.length > 0 && <ProblemList items={warnings} tone="warning" />}
    </div>
  );
}

// ─── Layout ─────────────────────────────────────────────────────────────────

interface LayoutCardProps {
  view: NodePoolsView;
  draft: NodePoolLayout | null;
  canEdit: boolean;
  saving: boolean;
  saveError: NodePoolsErrorBody | null;
  onEdit: () => void;
  onPropose: () => void;
  onChange: (l: NodePoolLayout) => void;
  onCancel: () => void;
  onSave: () => void;
}

function LayoutCard({ view, draft, canEdit, saving, saveError, onEdit, onPropose, onChange, onCancel, onSave }: LayoutCardProps) {
  const layout = draft ?? { pools: view.pools, assignments: view.assignments };
  const editing = draft !== null;
  const special = new Set(view.classes.special);

  const setPool = (idx: number, patch: Partial<NodePoolLayout['pools'][number]>) => {
    const pools = layout.pools.map((p, i) => (i === idx ? { ...p, ...patch } : p));
    let assignments = layout.assignments;
    if (patch.name !== undefined && patch.name !== layout.pools[idx].name) {
      const old = layout.pools[idx].name;
      assignments = Object.fromEntries(Object.entries(assignments).map(([m, p]) => [m, p === old ? patch.name! : p]));
    }
    onChange({ pools, assignments });
  };
  const toggleClass = (idx: number, cls: string) => {
    const accepts = layout.pools[idx].accepts;
    setPool(idx, { accepts: accepts.includes(cls) ? accepts.filter((c) => c !== cls) : [...accepts, cls] });
  };
  const removePool = (idx: number) => {
    const name = layout.pools[idx].name;
    onChange({
      pools: layout.pools.filter((_, i) => i !== idx),
      assignments: Object.fromEntries(Object.entries(layout.assignments).filter(([, p]) => p !== name)),
    });
  };
  const addPool = () => {
    let n = layout.pools.length + 1;
    while (layout.pools.some((p) => p.name === `pool-${n}`)) n++;
    onChange({ ...layout, pools: [...layout.pools, { name: `pool-${n}`, accepts: [], dedicated: false }] });
  };
  const assign = (machineId: string, pool: string) => {
    const assignments = { ...layout.assignments };
    if (pool) assignments[machineId] = pool; else delete assignments[machineId];
    onChange({ ...layout, assignments });
  };

  return (
    <div className={cn(CARD, 'p-5 space-y-4')} data-testid="node-pools-layout">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h4 className="text-sm font-semibold text-silver-bright">Layout</h4>
          <p className="text-[11px] text-silver-muted">
            {view.pools.length === 0
              ? 'No layout yet. Propose one from the boxes’ hardware, then adjust it.'
              : `Revision ${view.revision}. The installed products need ${view.requiredClasses.join(', ')}.`}
          </p>
        </div>
        {canEdit && !editing && (
          <div className="flex gap-2">
            <button type="button" className={SECONDARY} onClick={onPropose}><Wand2 size={12} className="inline mr-1" />Propose</button>
            <button type="button" className={SECONDARY} onClick={onEdit} disabled={view.pools.length === 0}>Edit</button>
          </div>
        )}
        {editing && (
          <div className="flex gap-2">
            <button type="button" className={SECONDARY} onClick={onCancel} disabled={saving}>Cancel</button>
            <button type="button" className={PRIMARY} onClick={onSave} disabled={saving}>{saving ? 'Saving…' : 'Save layout'}</button>
          </div>
        )}
      </div>

      {editing && view.stage !== 'none' && (
        <p className="text-[11px] text-status-warning">The site is at {STAGE_TEXT[view.stage].label}: a saved change reaches the cluster within a minute.</p>
      )}

      {saveError && (
        <div className="rounded-lg border border-status-error/40 bg-status-error/10 p-3 space-y-1" role="alert" data-testid="node-pools-save-error">
          <p className="text-xs text-status-error">{saveError.error}</p>
          {saveError.problems && <ProblemList items={saveError.problems} tone="error" />}
        </div>
      )}

      <div className="grid gap-3 md:grid-cols-2">
        {layout.pools.map((pool, idx) => {
          const members = Object.values(layout.assignments).filter((p) => p === pool.name).length;
          return (
            <div key={idx} className="rounded-lg border border-surface-elevated p-3 space-y-2" data-testid="node-pool-card">
              <div className="flex items-center gap-2">
                {editing ? (
                  <input
                    aria-label="pool name"
                    value={pool.name}
                    onChange={(e) => setPool(idx, { name: e.target.value })}
                    className="flex-1 bg-surface-deep border border-surface-elevated rounded px-2 py-1 text-sm font-mono text-silver-bright focus:border-gold focus:outline-none"
                  />
                ) : (
                  <span className="flex-1 font-mono text-sm text-silver-bright">{pool.name}</span>
                )}
                {pool.dedicated && !editing && (
                  <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-gold/10 text-gold border border-gold/20"><Lock size={10} />dedicated</span>
                )}
                <span className="text-[11px] text-silver-muted">{members} box{members === 1 ? '' : 'es'}</span>
                {editing && (
                  <button type="button" aria-label={`remove ${pool.name}`} onClick={() => removePool(idx)} className="p-1 text-silver-muted hover:text-status-error"><Trash2 size={13} /></button>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(editing ? view.classes.acceptable : pool.accepts).map((cls) => {
                  const on = pool.accepts.includes(cls);
                  return editing ? (
                    <label key={cls} className={cn('flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border cursor-pointer', on ? 'border-gold/50 text-gold' : 'border-surface-elevated text-silver-muted')} title={CLASS_DESCRIPTIONS[cls]}>
                      <input type="checkbox" className="sr-only" aria-label={`${pool.name} accepts ${cls}`} checked={on} onChange={() => toggleClass(idx, cls)} />
                      {cls}{special.has(cls) ? ' *' : ''}
                    </label>
                  ) : (
                    <span key={cls} className="px-2 py-0.5 rounded-full text-[11px] border border-gold/30 text-silver-bright" title={CLASS_DESCRIPTIONS[cls]}>{cls}</span>
                  );
                })}
              </div>
              {editing && (
                <label className="flex items-center gap-2 text-[11px] text-silver-muted">
                  <input type="checkbox" aria-label={`${pool.name} dedicated`} checked={pool.dedicated} onChange={(e) => setPool(idx, { dedicated: e.target.checked })} />
                  Dedicated: at the Dedicated stage only pods of the classes above schedule on these boxes
                </label>
              )}
            </div>
          );
        })}
        {editing && (
          <button type="button" onClick={addPool} className="rounded-lg border border-dashed border-surface-elevated p-3 text-xs text-gold hover:border-gold/60">
            <Plus size={12} className="inline mr-1" />Add pool
          </button>
        )}
      </div>
      {editing && view.classes.special.length > 0 && (
        <p className="text-[11px] text-silver-muted">* For one product&apos;s boxes ({view.classes.special.join(', ')}): give them a pool only when the site has that box.</p>
      )}

      <table className="w-full text-xs" data-testid="node-pools-machines">
        <thead>
          <tr className="text-left text-silver-muted border-b border-surface-elevated">
            <th className="py-1.5 font-medium">Box</th>
            <th className="py-1.5 font-medium">Hardware</th>
            <th className="py-1.5 font-medium">Pool</th>
          </tr>
        </thead>
        <tbody>
          {view.machines.map((m) => (
            <tr key={m.id} className="border-b border-surface-elevated/50">
              <td className="py-1.5">
                <span className="font-mono text-silver-bright">{m.hostname ?? m.id}</span>
                {m.role === 'controlplane' && <span className="ml-2 text-[10px] text-gold">control plane</span>}
                {!m.ready && <span className="ml-2 text-[10px] text-status-warning">not ready</span>}
              </td>
              <td className="py-1.5 text-silver-muted">{m.accelerator ? 'Accelerator (GPU / ConnectX)' : 'General'}</td>
              <td className="py-1.5">
                {editing ? (
                  <select
                    aria-label={`pool for ${m.hostname ?? m.id}`}
                    value={layout.assignments[m.id] ?? ''}
                    onChange={(e) => assign(m.id, e.target.value)}
                    className="bg-surface-deep border border-surface-elevated rounded px-2 py-1 text-xs text-silver-bright"
                  >
                    <option value="">— no pool —</option>
                    {layout.pools.map((p) => <option key={p.name} value={p.name}>{p.name}</option>)}
                  </select>
                ) : (
                  <span className="font-mono text-silver-bright">{view.assignments[m.id] ?? '—'}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Conformance ────────────────────────────────────────────────────────────

/** How long the list follows a Reschedule, waiting for the moved pods to leave it (JSS#1120). */
const RESCHEDULE_FOLLOW_MS = 120_000;
const RESCHEDULE_POLL_MS = 5_000;

const podKey = (i: { namespace: string; pod: string }) => `${i.namespace}/${i.pod}`;

function ConformanceCard({ siteId, view, canEdit }: { siteId: string; view: NodePoolsView; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  // The pods the last Reschedule moved, followed until they leave the list.
  const [following, setFollowing] = useState<{ keys: Set<string>; until: number } | null>(null);
  const { data, isFetching, error, refetch } = useQuery({
    queryKey: ['node-pools-conformance', siteId],
    queryFn: () => nodePoolsApi.conformance(siteId),
    enabled: open,
    // A restarted owner takes a while to replace its pod, so the old pod is
    // still listed straight after the request. Poll until every moved pod is
    // gone, for at most RESCHEDULE_FOLLOW_MS.
    refetchInterval: (query) =>
      following && Date.now() < following.until
        && (query.state.data?.items ?? []).some((i) => following.keys.has(podKey(i)))
        ? RESCHEDULE_POLL_MS
        : false,
  });
  // The header counts what the reconciler last observed. Keep an open list in
  // step with it, not with whenever the list was first fetched.
  const observedAt = view.status?.observedAt;
  useEffect(() => {
    if (open) void refetch();
  }, [observedAt]);
  const reschedule = useMutation({
    mutationFn: (items: { namespace: string; pod: string }[]) => nodePoolsApi.reschedule(siteId, items),
    onSuccess: (res) => {
      setPicked(new Set());
      setConfirming(false);
      const moved = res.results.filter((r) => r.action === 'restarted_owner' || r.action === 'deleted_pod');
      setFollowing({ keys: new Set(moved.map(podKey)), until: Date.now() + RESCHEDULE_FOLLOW_MS });
      void queryClient.invalidateQueries({ queryKey: ['node-pools-conformance', siteId] });
    },
  });
  if (view.stage === 'none' && !view.status?.conformance) return null;
  const misplaced = view.status?.conformance?.misplaced ?? null;
  const key = podKey;
  const toggle = (k: string) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });

  return (
    <div className={cn(CARD, 'p-5 space-y-3')} data-testid="node-pools-conformance">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h4 className="text-sm font-semibold text-silver-bright">Conformance</h4>
          <p className="text-[11px] text-silver-muted">
            {misplaced === null ? 'Pods the layout no longer places where they run.' : misplaced === 0
              ? 'Every pod runs where the layout places it.'
              : `${misplaced} pod${misplaced === 1 ? '' : 's'} run where the layout no longer places ${misplaced === 1 ? 'it' : 'them'}. Nothing is moved until you reschedule.`}
          </p>
        </div>
        <button type="button" className={SECONDARY} onClick={() => (open ? void refetch() : setOpen(true))} disabled={isFetching}>
          {open ? (isFetching ? 'Checking…' : 'Recheck') : 'Show pods'}
        </button>
      </div>
      {error && <p className="text-xs text-status-error" role="alert">{nodePoolsError(error).error}</p>}
      {open && data && (
        <>
          {data.items.length === 0 ? (
            <p className="text-xs text-silver-muted">No misplaced pods ({data.checked} checked).</p>
          ) : (
            <table className="w-full text-xs" data-testid="conformance-table">
              <thead>
                <tr className="text-left text-silver-muted border-b border-surface-elevated">
                  {canEdit && <th className="py-1.5 w-6" />}
                  <th className="py-1.5 font-medium">Pod</th>
                  <th className="py-1.5 font-medium">Node / pool</th>
                  <th className="py-1.5 font-medium">Class</th>
                  <th className="py-1.5 font-medium">Why</th>
                  <th className="py-1.5 font-medium">Owner</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((i) => (
                  <tr key={key(i)} className="border-b border-surface-elevated/50">
                    {canEdit && (
                      <td className="py-1.5">
                        <input type="checkbox" aria-label={`pick ${key(i)}`} checked={picked.has(key(i))} onChange={() => toggle(key(i))} disabled={i.reschedule === 'none'} />
                      </td>
                    )}
                    <td className="py-1.5 font-mono text-silver-bright">{key(i)}</td>
                    <td className="py-1.5 text-silver-muted">{i.node ?? '—'}{i.pool ? ` (${i.pool})` : ''}</td>
                    <td className="py-1.5 text-silver-muted">{i.workloadClass ?? '—'}</td>
                    <td className="py-1.5 text-silver-muted">{CONFORMANCE_REASON_TEXT[i.reason] ?? i.reason}</td>
                    <td className="py-1.5 text-silver-muted">
                      {i.owner ? `${i.owner.kind}/${i.owner.name}` : 'none'}
                      <span className="block text-[10px]">{RESCHEDULE_TEXT[i.reschedule]}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {canEdit && picked.size > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              {confirming ? (
                <>
                  <span className="text-xs text-status-warning">
                    Restart the owners of {picked.size} pod{picked.size === 1 ? '' : 's'}? A Deployment or StatefulSet rolls; any other controller&apos;s pod is deleted for it to recreate.
                  </span>
                  <button type="button" className={SECONDARY} onClick={() => setConfirming(false)}>Cancel</button>
                  <button
                    type="button"
                    className={PRIMARY}
                    disabled={reschedule.isPending}
                    onClick={() => reschedule.mutate([...picked].map((k) => { const [namespace, ...rest] = k.split('/'); return { namespace, pod: rest.join('/') }; }))}
                  >
                    {reschedule.isPending ? 'Rescheduling…' : 'Reschedule'}
                  </button>
                </>
              ) : (
                <button type="button" className={PRIMARY} onClick={() => setConfirming(true)}>Reschedule {picked.size} selected</button>
              )}
            </div>
          )}
          {reschedule.data && (
            <ul className="text-[11px] text-silver-muted space-y-0.5" data-testid="reschedule-results">
              {reschedule.data.results.map((r) => (
                <li key={key(r)}>
                  {key(r)}: {r.action === 'restarted_owner' ? `restarted ${r.owner?.kind}/${r.owner?.name}` : r.action === 'deleted_pod' ? 'deleted for its controller to recreate' : `${r.action} — ${r.reason}`}
                </li>
              ))}
            </ul>
          )}
          {reschedule.error && <p className="text-xs text-status-error" role="alert">{nodePoolsError(reschedule.error).error}</p>}
        </>
      )}
    </div>
  );
}
